import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Clock3,
  FileText,
  Package,
  ShoppingCart,
  Truck,
  TrendingUp,
  Star,
  Building2,
  ChevronRight,
  TrendingDown,
  Minus,
  Send,
  Plus,
  X,
} from "lucide-react";
import { FaPlus } from "react-icons/fa";
import { BASE_URL } from "utils/constant";
import Button from "components/ui-custom/Button";

const METRIC_CARD_TEMPLATES = [
  {
    label: "Pending Approvals",
    helper: "needs attention",
    icon: Clock3,
    iconBg: "bg-[#FEF6E6]",
    iconColor: "text-[#ECA600]",
    helperColor: "text-[#ECA600]",
  },
  {
    label: "RFQs In Progress",
    helper: "awaiting vendors",
    icon: FileText,
    iconBg: "bg-[#ECF3FF]",
    iconColor: "text-[#3B82F6]",
    helperColor: "text-[#3B82F6]",
  },
  {
    label: "Open Purchase Orders",
    helper: "active orders",
    icon: ShoppingCart,
    iconBg: "bg-[#EAF9F0]",
    iconColor: "text-[#22A55D]",
    helperColor: "text-[#22A55D]",
  },
  {
    label: "Delayed Deliveries",
    helper: "need follow-up",
    icon: Truck,
    iconBg: "bg-[#F2ECFF]",
    iconColor: "text-[#8759E0]",
    helperColor: "text-[#8759E0]",
  },
  {
    label: "Monthly Spend",
    helper: "within budget",
    icon: TrendingUp,
    iconBg: "bg-[#EAF9F0]",
    iconColor: "text-[#22A55D]",
    helperColor: "text-[#22A55D]",
  },
];

const STATIC_INSIGHT_CARDS = [
  {
    title: "Avg Procurement Time",
    icon: Clock3,
    iconColor: "text-[#3B82F6]",
    bigValue: "4.2 days",
    subText: "Request to delivery",
    lines: [
      { label: "Request → RFQ", value: "0.5 days" },
      { label: "RFQ → PO", value: "1.5 days" },
      { label: "PO → Delivery", value: "2.2 days" },
    ],
  },
  {
    title: "Vendor Performance",
    icon: Star,
    iconColor: "text-[#D97706]",
    bigValue: "4.3 / 5",
    subText: "Overall rating",
    lines: [
      { label: "On-time delivery", value: "92%", valueColor: "text-[#16A34A]" },
      { label: "Quality score", value: "88%", valueColor: "text-[#16A34A]" },
      {
        label: "Price competitiveness",
        value: "76%",
        valueColor: "text-[#D97706]",
      },
    ],
  },
];

const ALERTS_DISPLAY_LIMIT = 5;

const CLOSED_PO_STATUSES = new Set([
  "completed",
  "cancelled",
  "canceled",
  "closed",
]);

const getValidDate = (value) => {
  if (!value) return null;

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const formatCompactNumber = (value) => {
  const absoluteValue = Math.abs(Number(value) || 0);

  if (absoluteValue >= 10000000) {
    return `${(absoluteValue / 10000000).toFixed(1).replace(/\.0$/, "")}Cr`;
  }

  if (absoluteValue >= 100000) {
    return `${(absoluteValue / 100000).toFixed(1).replace(/\.0$/, "")}L`;
  }

  if (absoluteValue >= 1000) {
    return `${(absoluteValue / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  }

  return absoluteValue.toLocaleString("en-IN");
};

const formatCurrencyCompact = (value) =>
  `₹${formatCompactNumber(Number(value) || 0)}`;

const formatCurrency = (value) =>
  `₹${(Number(value) || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  })}`;

const formatQuantity = (value, unit = "") => {
  const numericValue = Number(value) || 0;
  const formattedValue = Number.isInteger(numericValue)
    ? numericValue.toLocaleString("en-IN")
    : numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

  return `${formattedValue}${unit ? ` ${unit}` : ""}`;
};

const formatAlertDate = (value) => {
  if (!value) return "N/A";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getMRMaterialSummary = (materialRequest) => {
  const lines = Array.isArray(materialRequest?.material_request_lines)
    ? materialRequest.material_request_lines
    : [];

  if (lines.length === 0) {
    return "";
  }

  const firstMaterialName =
    lines[0]?.material_name || lines[0]?.name || "Material";
  const extraMaterialsCount = lines.length - 1;

  return extraMaterialsCount > 0
    ? `${firstMaterialName} + ${extraMaterialsCount} materials`
    : firstMaterialName;
};

const normalizeAttentionItems = (alertPayload, requests = []) => {
  const requestLookup = requests.reduce((accumulator, request) => {
    accumulator[String(request.id)] = request;
    return accumulator;
  }, {});

  const pendingMaterialRequests =
    alertPayload?.pending_material_requests?.results?.map((item) => ({
      id: item.id,
      type: "mr",
      typeLabel: "Material Request",
      badge: "Awaiting Approval",
      badgeClass: "bg-[#FFF4DE] text-[#D97706]",
      title: item.mr_number || `MR-${item.id}`,
      summary:
        getMRMaterialSummary(item) ||
        getMRMaterialSummary(requestLookup[String(item.id)]),
      meta: item.site_name || "Warehouse",
      owner: item.created_by,
      date: formatAlertDate(item.created_on),
      created_on: item.created_on,
    })) || [];

  const openPurchaseOrders =
    alertPayload?.open_purchase_orders?.results?.map((item) => ({
      id: item.id,
      type: "po",
      typeLabel: "Purchase Order",
      badge: "Open",
      badgeClass: "bg-[#ECFDF3] text-[#027A48]",
      title: item.po_number || `PO-${item.id}`,
      meta: item.site_name || "Warehouse",
      owner: item.vendor_name ? `Vendor: ${item.vendor_name}` : item.created_by,
      date: formatAlertDate(item.created_on),
      created_on: item.created_on,
    })) || [];

  const rfqsInProgress =
    alertPayload?.rfq_without_responses?.results?.map((item) => ({
      id: item.id,
      type: "rfq",
      typeLabel: "RFQ",
      badge: "No Responses",
      badgeClass: "bg-[#F4F4F5] text-[#52525B]",
      title: item.rfq_number || `RFQ-${item.id}`,
      meta: item.site_name || "Warehouse",
      owner: item.material_request_number
        ? `MR: ${item.material_request_number}`
        : item.created_by,
      date: formatAlertDate(item.created_on),
      created_on: item.created_on,
    })) || [];

  const delayedDeliveries =
    alertPayload?.delayed_deliveries?.results?.map((item) => ({
      id: item.id,
      type: "po",
      typeLabel: "Purchase Order",
      badge: "Delayed",
      badgeClass: "bg-[#FEE2E2] text-[#B91C1C]",
      title: item.po_number || `PO-${item.id}`,
      meta: item.site_name || "Warehouse",
      owner: item.vendor_name ? `Vendor: ${item.vendor_name}` : item.created_by,
      date: formatAlertDate(item.created_on),
      created_on: item.created_on,
    })) || [];

  return [
    ...pendingMaterialRequests,
    ...openPurchaseOrders,
    ...rfqsInProgress,
    ...delayedDeliveries,
  ];
};

function MetricCard({ card }) {
  const Icon = card.icon;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-3 flex flex-row gap-3 items-center justify-start">
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center ${card.iconBg}`}
      >
        <Icon size={18} className={card.iconColor} />
      </div>
      <div className="flex flex-col items-start justify-center gap-1">
        <p className="text-2xl font-semibold text-[#232323] leading-none">
          {card.value}
        </p>
        <p className="text-sm text-[#4B5563] leading-tight">{card.label}</p>
        <p className={`text-xs ${card.helperColor}`}>{card.helper}</p>
      </div>
    </div>
  );
}

export default function ProcurementHomeDashboard({
  organisationId,
  onCreateRequest,
  showCreatePanel = false,
  onCloseCreatePanel,
  onCreateMR,
  onCreateRFQ,
  onCreatePO,
  onViewAlert,
  requests = [],
  rfqs = [],
  purchaseOrders = [],
  showHeader = true,
  showCreateButton = true,
}) {
  const createPanelRef = useRef(null);
  const [rawAttentionItems, setRawAttentionItems] = useState([]);
  const [alertSortOrder, setAlertSortOrder] = useState("oldest");
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const getProcurementAlerts = async () => {
      if (!organisationId) {
        return;
      }

      setAlertsLoading(true);
      setAlertsError("");

      try {
        const response = await fetch(
          `${BASE_URL}/inventory/procurement-alerts/org/${organisationId}/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch procurement alerts");
        }

        const payload = await response.json();
        const alertData = payload?.data || {};

        if (!isMounted) {
          return;
        }
        setRawAttentionItems(normalizeAttentionItems(alertData, requests));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRawAttentionItems([]);
        setAlertsError("Unable to load procurement alerts right now.");
        console.error("Error fetching procurement alerts:", error);
      } finally {
        if (isMounted) {
          setAlertsLoading(false);
        }
      }
    };

    getProcurementAlerts();

    return () => {
      isMounted = false;
    };
  }, [organisationId, requests]);

  const attentionItems = useMemo(() => {
    return [...rawAttentionItems]
      .sort((firstItem, secondItem) => {
        const firstDate = new Date(firstItem.created_on || 0).getTime();
        const secondDate = new Date(secondItem.created_on || 0).getTime();

        return alertSortOrder === "newest"
          ? secondDate - firstDate
          : firstDate - secondDate;
      })
      .slice(0, ALERTS_DISPLAY_LIMIT);
  }, [alertSortOrder, rawAttentionItems]);

  const dashboardMetrics = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const pendingApprovals = requests.filter(
      (request) => request.status === "PENDING_APPROVAL",
    ).length;

    const rfqsInProgress = rfqs.filter((rfq) => rfq.status === "OPEN").length;

    const openPurchaseOrders = purchaseOrders.filter(
      (po) =>
        (po.status || "").toLowerCase() === "approved" ||
        (po.status || "").toLowerCase() === "open",
    ).length;

    const dueDeliveries = purchaseOrders.filter((po) => {
      const status = (po.status || "").toLowerCase();
      const orderDate = getValidDate(po.order_date || po.created_on);

      return orderDate && orderDate < today && !CLOSED_PO_STATUSES.has(status);
    }).length;

    const monthlySpend = purchaseOrders.reduce((totalAmount, po) => {
      const orderDate = getValidDate(po.order_date || po.created_on);
      if (
        !orderDate ||
        orderDate.getMonth() !== today.getMonth() ||
        orderDate.getFullYear() !== today.getFullYear()
      ) {
        return totalAmount;
      }

      return totalAmount + (Number(po.paid_amount) || 0);
    }, 0);

    return {
      pendingApprovals,
      rfqsInProgress,
      openPurchaseOrders,
      dueDeliveries,
      monthlySpend,
    };
  }, [purchaseOrders, requests, rfqs]);

  const metricCards = useMemo(() => {
    const valuesByLabel = {
      "Pending Approvals": String(dashboardMetrics.pendingApprovals),
      "RFQs In Progress": String(dashboardMetrics.rfqsInProgress),
      "Open Purchase Orders": String(dashboardMetrics.openPurchaseOrders),
      "Due Deliveries": String(dashboardMetrics.dueDeliveries),
      "Monthly Spend": formatCurrencyCompact(dashboardMetrics.monthlySpend),
    };

    return METRIC_CARD_TEMPLATES.map((card) => ({
      ...card,
      label:
        card.label === "Delayed Deliveries" ? "Due Deliveries" : card.label,
      helper:
        card.label === "Monthly Spend"
          ? "current month"
          : card.label === "Delayed Deliveries"
            ? "past order date"
            : card.helper,
      value:
        valuesByLabel[
          card.label === "Delayed Deliveries" ? "Due Deliveries" : card.label
        ] || "0",
    }));
  }, [dashboardMetrics]);

  const pipelineStages = useMemo(
    () => [
      {
        label: "Material Request",
        short: "Request",
        value: requests.length,
        color: "text-[#3B82F6]",
        bg: "bg-[#ECF3FF]",
        icon: FileText,
      },
      {
        label: "RFQ",
        short: "RFQ",
        value: rfqs.length,
        color: "text-[#8759E0]",
        bg: "bg-[#F2ECFF]",
        icon: Send,
      },
      {
        label: "Purchase Order",
        short: "PO",
        value: purchaseOrders.length,
        color: "text-[#5B21B6]",
        bg: "bg-[#F3E8FF]",
        icon: ShoppingCart,
      },
      {
        label: "In Transit",
        short: "Transit",
        value: purchaseOrders.filter(
          (po) => (po.status || "").toLowerCase() === "approved",
        ).length,
        color: "text-[#D97706]",
        bg: "bg-[#FFF4DE]",
        icon: Truck,
      },
      {
        label: "Stocked",
        short: "Stocked",
        value: purchaseOrders.filter(
          (po) => (po.status || "").toLowerCase() === "completed",
        ).length,
        color: "text-[#16A34A]",
        bg: "bg-[#EAF9F0]",
        icon: Package,
      },
    ],
    [purchaseOrders, requests.length, rfqs.length],
  );

  const insightCards = useMemo(() => {
    const vendorTotals = purchaseOrders.reduce((accumulator, po) => {
      const vendorName = po.vendor_name || "Unknown Vendor";
      accumulator[vendorName] =
        (accumulator[vendorName] || 0) + (Number(po.paid_amount) || 0);
      return accumulator;
    }, {});

    const vendorEntries = Object.entries(vendorTotals).sort(
      (firstVendor, secondVendor) => secondVendor[1] - firstVendor[1],
    );

    const topVendorEntries = vendorEntries.slice(0, 4);
    const maxVendorSpend = topVendorEntries[0]?.[1] || 0;

    const spendByVendorLines =
      topVendorEntries.length > 0
        ? topVendorEntries.map(([vendorName, totalAmount]) => ({
            label: vendorName,
            value: formatCurrency(totalAmount),
            progressPercent: maxVendorSpend
              ? (totalAmount / maxVendorSpend) * 100
              : 0,
          }))
        : [
            {
              label: "No vendor payments yet",
              value: "₹0",
              progressPercent: 0,
            },
          ];

    const materialTotals = purchaseOrders.reduce((accumulator, po) => {
      (po.lines || []).forEach((line) => {
        const materialName = line.material_name || "Unknown Material";
        const quantity = Number(line.quantity) || 0;
        const existingMaterial = accumulator[materialName] || {
          quantity: 0,
          uom: line.uom || line.unit || "",
        };

        accumulator[materialName] = {
          quantity: existingMaterial.quantity + quantity,
          uom: existingMaterial.uom || line.uom || line.unit || "",
        };
      });

      return accumulator;
    }, {});

    const topMaterialsLines = Object.entries(materialTotals)
      .sort((firstMaterial, secondMaterial) => {
        return secondMaterial[1].quantity - firstMaterial[1].quantity;
      })
      .slice(0, 4)
      .map(([materialName, materialData]) => ({
        label: materialName,
        subValue: formatQuantity(materialData.quantity, materialData.uom),
      }));

    return [
      {
        title: "Spend by Vendor",
        icon: Building2,
        iconColor: "text-[#8759E0]",
        lines: spendByVendorLines,
      },
      {
        title: "Top Materials",
        icon: Package,
        iconColor: "text-[#8759E0]",
        lines:
          topMaterialsLines.length > 0
            ? topMaterialsLines
            : [{ label: "No materials yet", subValue: "0" }],
      },
      // ...STATIC_INSIGHT_CARDS,
    ];
  }, [purchaseOrders]);

  useEffect(() => {
    if (!showCreatePanel) return;

    const handleOutsideClick = (event) => {
      if (
        createPanelRef.current &&
        !createPanelRef.current.contains(event.target)
      ) {
        onCloseCreatePanel?.();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showCreatePanel, onCloseCreatePanel]);

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        {showHeader ? (
          <div>
            <h2 className="text-2xl leading-tight font-semibold text-[#232323]">
              Procurement Dashboard
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Track materials, orders, and deliveries across all projects
            </p>
          </div>
        ) : (
          <div />
        )}
        <div className="relative" ref={createPanelRef}>
          {showCreateButton && (
            <Button
              onClick={onCreateRequest}
              className="bg-sm-primary hover:bg-sm-primary/90 text-white h-9 text-sm gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Request
            </Button>
          )}

          {showCreateButton && showCreatePanel && (
            <div className="create-request-panel absolute right-0 top-full mt-2 w-[320px] bg-white border border-gray-200 rounded-xl shadow-xl z-30 p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-[#232323]">
                  Create Request
                </h3>
                <button
                  onClick={onCloseCreatePanel}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
                  aria-label="Close create request options"
                >
                  <X size={16} />
                </button>
              </div>

              <button
                onClick={onCreateMR}
                className="w-full flex items-center justify-between px-3 py-3 rounded-lg bg-[#F5EEF9] text-[#3B0069] mb-2 border border-[#E7D9F0] hover:bg-[#EFE4F5]"
              >
                <span className="text-sm font-medium whitespace-nowrap">
                  Material Request
                </span>
                <span className="w-6 h-6 rounded-full bg-[#3B0069] flex items-center justify-center">
                  <FaPlus size={12} className="text-white" />
                </span>
              </button>
              <button
                onClick={onCreateRFQ}
                className="w-full flex items-center justify-between px-3 py-3 rounded-lg bg-[#F5EEF9] text-[#3B0069] border border-[#E7D9F0] hover:bg-[#EFE4F5]"
              >
                <span className="text-sm font-medium">Raise RFQ</span>
                <span className="w-6 h-6 rounded-full bg-[#3B0069] flex items-center justify-center">
                  <FaPlus size={12} className="text-white" />
                </span>
              </button>

              <button
                onClick={onCreatePO}
                className="w-full flex items-center justify-between px-3 py-3 rounded-lg bg-[#F5EEF9] text-[#3B0069] mb-2 border border-[#E7D9F0] hover:bg-[#EFE4F5]"
              >
                <span className="text-sm font-medium">Purchase Order (PO)</span>
                <span className="w-6 h-6 rounded-full bg-[#3B0069] flex items-center justify-center">
                  <FaPlus size={12} className="text-white" />
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5  gap-3">
        {metricCards.map((card) => (
          <MetricCard key={card.label} card={card} />
        ))}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <div className="mb-1 flex items-center justify-between gap-3 px-2">
          <div className="flex items-center gap-2">
            <span className="text-[#D97706] text-sm bg-red-500 rounded-full p-1" />
            <h3 className="text-[18px] font-semibold text-[#232323]">
              Needs Your Attention
            </h3>
            <span className="bg-[#EEE8FF] text-[#5B21B6] text-xs font-medium px-2 py-0.5 rounded-full">
              {alertsLoading ? "Loading..." : `${attentionItems.length} alerts`}
            </span>
          </div>
          <div className="inline-flex rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-1">
            {["oldest", "newest"].map((sortOption) => (
              <button
                key={sortOption}
                type="button"
                onClick={() => setAlertSortOrder(sortOption)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                  alertSortOrder === sortOption
                    ? "bg-white text-[#3B0069] shadow-sm"
                    : "text-[#6B7280] hover:text-[#232323]"
                }`}
              >
                {sortOption}
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-[#6B7280] mb-3 px-2">
          Top {ALERTS_DISPLAY_LIMIT} {alertSortOrder} procurement alerts across
          requests, RFQs, and purchase orders
        </p>

        <div className="space-y-3">
          {alertsLoading ? (
            <div className="border border-[#E5E7EB] rounded-xl px-4 py-8 text-sm text-[#6B7280] text-center">
              Loading procurement alerts...
            </div>
          ) : alertsError ? (
            <div className="border border-[#FECACA] bg-[#FEF2F2] rounded-xl px-4 py-4 text-sm text-[#B91C1C]">
              {alertsError}
            </div>
          ) : attentionItems.length === 0 ? (
            <div className="border border-[#E5E7EB] rounded-xl px-4 py-8 text-sm text-[#6B7280] text-center">
              No procurement alerts right now.
            </div>
          ) : (
            attentionItems.map((item) => (
              <div
                key={`${item.type}-${item.id}`}
                className="border border-[#E5E7EB] rounded-xl px-4 py-3 flex items-center justify-between gap-3"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-xs tracking-wide uppercase text-[#9CA3AF]">
                      {item.typeLabel}
                    </p>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.badgeClass}`}
                    >
                      {item.badge}
                    </span>
                  </div>
                  <p className="text-[16px] font-semibold text-[#232323] mt-1">
                    {item.title}
                  </p>
                  {item.type === "mr" && item.summary ? (
                    <p className="text-sm text-[#4B5563] mt-0.5">
                      {item.summary}
                    </p>
                  ) : null}
                  <p className="text-sm text-[#6B7280] mt-0.5">
                    {[item.meta, item.owner, item.date]
                      .filter(Boolean)
                      .join(" • ")}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onViewAlert?.(item)}
                    className="px-4 h-[36px] flex items-center justify-center text-sm border border-[#D1D5DB] rounded-md text-[#4B5563] bg-white hover:bg-gray-50 transition-colors"
                  >
                    View
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <h3 className="text-[18px] font-semibold text-[#232323]">
          Procurement Pipeline
        </h3>
        <p className="text-sm text-[#6B7280] mb-3">
          Track materials through each stage
        </p>

        <div className="flex items-center justify-between mb-4 relative">
          {pipelineStages.map((stage, index) => {
            const Icon = stage.icon;
            return (
              <div
                key={stage.label}
                className="flex flex-col items-center justify-center w-[20%] gap-1.5 relative"
              >
                <span
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${stage.bg} z-10`}
                >
                  <Icon size={18} className={stage.color} />
                </span>
                <div className="text-center">
                  <p className="text-[18px] leading-none font-semibold text-[#232323]">
                    {stage.value}
                  </p>
                  <p className="text-[11px] text-[#6B7280] mt-0.5">
                    {stage.label}
                  </p>
                </div>
                {index < pipelineStages.length - 1 && (
                  <div className="absolute right-[-10px] top-5 transform -translate-y-1/2 z-0">
                    <ChevronRight size={18} className="text-[#D1D5DB]" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="w-full h-1.5 rounded-full overflow-hidden flex">
          <span className="h-full w-[20%] bg-[#3B82F6]" />
          <span className="h-full w-[20%] bg-[#8759E0]" />
          <span className="h-full w-[20%] bg-[#5B21B6]" />
          <span className="h-full w-[20%] bg-[#D97706]" />
          <span className="h-full w-[20%] bg-[#16A34A]" />
        </div>

        <div className="flex justify-between mt-1.5 text-[10px] text-[#6B7280]">
          {pipelineStages.map((stage) => (
            <p key={stage.short} className="w-[20%] text-center">
              {stage.short}
            </p>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-3">
        {insightCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className="bg-white border border-[#E5E7EB] rounded-xl p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon size={16} className={card.iconColor} />
                <h4 className="text-[15px] font-semibold text-[#232323]">
                  {card.title}
                </h4>
              </div>

              {card.bigValue ? (
                <div className="mb-4">
                  <p className="text-[32px] leading-none font-semibold text-[#232323]">
                    {card.bigValue}
                  </p>
                  {card.subText && (
                    <p className="text-sm text-[#6B7280] mt-1">
                      {card.subText}
                    </p>
                  )}
                </div>
              ) : null}

              <div className="space-y-3">
                {card.lines.map((line) => (
                  <div key={`${card.title}-${line.label}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-[#4B5563]">
                          {line.label}
                        </span>
                        {line.subValue && (
                          <span className="text-xs text-[#6B7280]">
                            {line.subValue}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {line.value && (
                          <span
                            className={`text-sm font-semibold ${line.valueColor || "text-[#232323]"}`}
                          >
                            {line.value}
                          </span>
                        )}
                        {line.trend === "up" && (
                          <TrendingUp size={16} className="text-[#16A34A]" />
                        )}
                        {line.trend === "down" && (
                          <TrendingDown size={16} className="text-[#EF4444]" />
                        )}
                        {line.trend === "neutral" && (
                          <Minus size={16} className="text-[#6B7280]" />
                        )}
                      </div>
                    </div>
                    {line.progress && (
                      <div className="w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-[#8759E0] rounded-full ${line.progress}`}
                        />
                      </div>
                    )}
                    {typeof line.progressPercent === "number" && (
                      <div className="w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#8759E0] rounded-full"
                          style={{
                            width: `${Math.max(line.progressPercent, 0)}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
